import { History as HistoryIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { itemStatusService } from "../../../services/itemStatusService";
import { ItemStatus } from "../../../types/admin";

interface StatusUpdateModalProps {
  open: boolean;
  onClose: () => void;
  orderItem: {
    id: string;
    title: string;
    currentStatusId?: number;
    currentStatusName?: string;
  } | null;
  onStatusUpdate: (
    orderItemId: string,
    newStatusId: number,
    notes?: string,
    sendNotification?: boolean,
  ) => Promise<void>;
}

const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
  open,
  onClose,
  orderItem,
  onStatusUpdate,
}) => {
  const { t } = useTranslation();
  const [allStatuses, setAllStatuses] = useState<ItemStatus[]>([]);
  const [nextAllowedStatuses, setNextAllowedStatuses] = useState<ItemStatus[]>(
    [],
  );
  const [selectedStatusId, setSelectedStatusId] = useState<number | "">("");
  const [notes, setNotes] = useState("");
  const [sendNotification, setSendNotification] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllStatuses, setShowAllStatuses] = useState(false);

  useEffect(() => {
    if (open && orderItem) {
      loadStatuses();
    }
  }, [open, orderItem]);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all statuses
      const allStatusesData = await itemStatusService.getAllItemStatuses();
      setAllStatuses(allStatusesData);

      // Load next allowed statuses if we have a current status
      if (orderItem?.currentStatusId) {
        const nextStatuses = await itemStatusService.getNextAllowedStatuses(
          orderItem.currentStatusId,
        );
        setNextAllowedStatuses(nextStatuses);

        // If no next allowed statuses, show all statuses
        if (nextStatuses.length === 0) {
          setShowAllStatuses(true);
        }
      } else {
        setShowAllStatuses(true);
      }
    } catch (error) {
      setError(t("admin.failedToLoadStatuses"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!orderItem || !selectedStatusId) return;

    try {
      setLoading(true);
      setError(null);

      await onStatusUpdate(
        orderItem.id,
        selectedStatusId,
        notes.trim() || undefined,
        sendNotification,
      );

      // Reset form and close modal
      setSelectedStatusId("");
      setNotes("");
      setSendNotification(true);
      onClose();
    } catch (error) {
      setError(t("admin.failedToUpdateStatus"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedStatusId("");
    setNotes("");
    setSendNotification(true);
    setError(null);
    setShowAllStatuses(false);
    onClose();
  };

  const getStatusColor = (status: ItemStatus) => {
    // Try to parse the color from the status, fallback to default colors
    if (status.color && status.color.startsWith("#")) {
      return status.color;
    }

    // Default color mapping based on status name
    const statusName = status.name.toLowerCase();
    if (statusName.includes("pending")) return "#ff9800";
    if (statusName.includes("processing")) return "#2196f3";
    if (statusName.includes("ordered")) return "#4caf50";
    if (statusName.includes("shipped")) return "#9c27b0";
    if (statusName.includes("delivered")) return "#4caf50";
    if (statusName.includes("cancelled")) return "#f44336";
    if (statusName.includes("hold")) return "#ff9800";

    return "#757575";
  };

  const displayStatuses = showAllStatuses ? allStatuses : nextAllowedStatuses;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HistoryIcon />
          {t("admin.updateItemStatus")}
        </Box>
      </DialogTitle>

      <DialogContent>
        {orderItem && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {orderItem.title}
            </Typography>
            {orderItem.currentStatusName && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t("admin.currentStatus")}
                </Typography>
                <Chip
                  label={orderItem.currentStatusName}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              </Box>
            )}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>{t("admin.newStatus")}</InputLabel>
              <Select
                value={selectedStatusId}
                onChange={(e) => setSelectedStatusId(e.target.value as number)}
                label={t("admin.newStatus")}
                disabled={loading}
              >
                {loading ? (
                  <MenuItem value="" disabled>
                    {t("admin.loadingStatuses")}
                  </MenuItem>
                ) : displayStatuses.length > 0 ? (
                  displayStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: "50%",
                            backgroundColor: getStatusColor(status),
                          }}
                        />
                        <Typography>{status.displayName}</Typography>
                        {status.description && (
                          <Typography variant="caption" color="text.secondary">
                            - {status.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    {t("admin.noStatusesAvailable")}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("admin.notesOptional")}
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              placeholder={t("admin.statusChangeNotesPlaceholder")}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendNotification}
                  onChange={(e) => setSendNotification(e.target.checked)}
                  disabled={loading}
                />
              }
              label={t("admin.sendEmailNotification")}
            />
          </Grid>

          {!showAllStatuses && nextAllowedStatuses.length > 0 && (
            <Grid item xs={12}>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowAllStatuses(true)}
                sx={{ mt: 1 }}
              >
                {t("admin.showAllStatuses")}
              </Button>
            </Grid>
          )}

          {showAllStatuses && (
            <Grid item xs={12}>
              <Button
                variant="text"
                size="small"
                onClick={() => setShowAllStatuses(false)}
                sx={{ mt: 1 }}
              >
                {t("admin.showOnlyAllowedTransitions")}
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selectedStatusId}
        >
          {loading ? t("admin.updating") : t("admin.updateStatus")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusUpdateModal;
